// YDG WRLD JEWELS — Real Shopify Storefront API cart integration.
// Replaces the old localStorage-only cart with an actual Shopify Cart
// object (via Storefront GraphQL API), so checkout hands off to a real
// Shopify-hosted checkout URL with the correct items/prices/inventory.
//
// Public API (attached to window.YDGCart):
//   YDGCart.add(variantId, qty)        -> Promise<cart>
//   YDGCart.updateLine(lineId, qty)    -> Promise<cart>
//   YDGCart.removeLine(lineId)         -> Promise<cart>
//   YDGCart.get()                      -> Promise<cart>  (fetches fresh from Shopify)
//   YDGCart.getCached()                -> cart | null     (last known state, sync)
//   YDGCart.getCheckoutUrl()           -> string | null
//   YDGCart.onChange(fn)               -> subscribe to cart updates
//
// Cart shape returned matches Shopify's Cart object (id, checkoutUrl,
// totalQuantity, cost.subtotalAmount, lines.edges[].node{id, quantity,
// merchandise{...}, cost}).

(function (window) {
  'use strict';

  const SHOPIFY_DOMAIN = 'ydgjewels.com';
  const STOREFRONT_TOKEN = '7178be40cdaa649752bbbd04dc0ea462';
  const API_VERSION = '2024-10';
  const ENDPOINT = 'https://' + SHOPIFY_DOMAIN + '/api/' + API_VERSION + '/graphql.json';
  const CART_ID_KEY = 'ydg_shopify_cart_id';

  let _cachedCart = null;
  const _listeners = [];

  function notify() {
    _listeners.forEach(function (fn) {
      try { fn(_cachedCart); } catch (e) { /* no-op */ }
    });
  }

  function gql(query, variables) {
    return fetch(ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
      },
      body: JSON.stringify({ query: query, variables: variables || {} }),
    })
      .then(function (res) { return res.json(); })
      .then(function (json) {
        if (json.errors) {
          console.error('[YDGCart] GraphQL errors:', json.errors);
          throw new Error(json.errors[0] && json.errors[0].message || 'Shopify GraphQL error');
        }
        return json.data;
      });
  }

  const CART_FIELDS = `
    id
    checkoutUrl
    totalQuantity
    cost {
      subtotalAmount { amount currencyCode }
      totalAmount { amount currencyCode }
    }
    lines(first: 100) {
      edges {
        node {
          id
          quantity
          cost { totalAmount { amount currencyCode } }
          merchandise {
            ... on ProductVariant {
              id
              title
              image { url altText }
              product { title handle }
              price { amount currencyCode }
            }
          }
        }
      }
    }
  `;

  function setCart(cart) {
    _cachedCart = cart;
    if (cart && cart.id) {
      try { localStorage.setItem(CART_ID_KEY, cart.id); } catch (e) { /* no-op */ }
    }
    notify();
    return cart;
  }

  function getStoredCartId() {
    try { return localStorage.getItem(CART_ID_KEY); } catch (e) { return null; }
  }

  function createCart(lines) {
    const query = `
      mutation cartCreate($lines: [CartLineInput!]) {
        cartCreate(input: { lines: $lines }) {
          cart { ${CART_FIELDS} }
          userErrors { field message }
        }
      }
    `;
    return gql(query, { lines: lines || [] }).then(function (data) {
      const result = data.cartCreate;
      if (result.userErrors && result.userErrors.length) {
        console.error('[YDGCart] cartCreate userErrors:', result.userErrors);
      }
      return setCart(result.cart);
    });
  }

  function fetchCart(cartId) {
    const query = `
      query cart($id: ID!) {
        cart(id: $id) { ${CART_FIELDS} }
      }
    `;
    return gql(query, { id: cartId }).then(function (data) {
      // Cart may be null if it expired/was completed — caller should
      // fall back to creating a new one.
      return setCart(data.cart);
    });
  }

  function ensureCart() {
    if (_cachedCart && _cachedCart.id) return Promise.resolve(_cachedCart);
    const storedId = getStoredCartId();
    if (!storedId) return createCart([]);
    return fetchCart(storedId).then(function (cart) {
      if (cart) return cart;
      return createCart([]);
    }).catch(function () {
      return createCart([]);
    });
  }

  function add(variantId, qty) {
    qty = qty || 1;
    return ensureCart().then(function (cart) {
      const query = `
        mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
          cartLinesAdd(cartId: $cartId, lines: $lines) {
            cart { ${CART_FIELDS} }
            userErrors { field message }
          }
        }
      `;
      return gql(query, {
        cartId: cart.id,
        lines: [{ merchandiseId: variantId, quantity: qty }],
      }).then(function (data) {
        const result = data.cartLinesAdd;
        if (result.userErrors && result.userErrors.length) {
          console.error('[YDGCart] cartLinesAdd userErrors:', result.userErrors);
        }
        return setCart(result.cart);
      });
    });
  }

  function updateLine(lineId, qty) {
    return ensureCart().then(function (cart) {
      const query = `
        mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
          cartLinesUpdate(cartId: $cartId, lines: $lines) {
            cart { ${CART_FIELDS} }
            userErrors { field message }
          }
        }
      `;
      return gql(query, {
        cartId: cart.id,
        lines: [{ id: lineId, quantity: qty }],
      }).then(function (data) {
        return setCart(data.cartLinesUpdate.cart);
      });
    });
  }

  function removeLine(lineId) {
    return ensureCart().then(function (cart) {
      const query = `
        mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
          cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
            cart { ${CART_FIELDS} }
            userErrors { field message }
          }
        }
      `;
      return gql(query, { cartId: cart.id, lineIds: [lineId] }).then(function (data) {
        return setCart(data.cartLinesRemove.cart);
      });
    });
  }

  function get() {
    return ensureCart();
  }

  function getCached() {
    return _cachedCart;
  }

  function getCheckoutUrl() {
    return _cachedCart ? _cachedCart.checkoutUrl : null;
  }

  function onChange(fn) {
    if (typeof fn === 'function') _listeners.push(fn);
  }

  window.YDGCart = {
    add: add,
    updateLine: updateLine,
    removeLine: removeLine,
    get: get,
    getCached: getCached,
    getCheckoutUrl: getCheckoutUrl,
    onChange: onChange,
  };

  // Warm the cache on load so badge counts etc. are ready quickly.
  ensureCart().catch(function (e) { console.error('[YDGCart] init failed:', e); });

})(window);
